import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Avatar,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  ContentCopy as CopyIcon,
  Info as InfoIcon,
  ArrowBack as BackIcon,
  Build as BuildIcon,
  Timeline as TimelineIcon,
  Category as CategoryIcon,
  Extension as ExtensionIcon,
  Close as CloseIcon,
  Tag as TagIcon,
  MonetizationOn as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Description as DescriptionIcon,
  AccountTree as AccountTreeIcon,
  Psychology as ComplexityIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { listSystemTemplates, cloneWorkflow, getRequiredConnections } from '../../store/Workflows';
import { getIntegrationTools } from '../../store/Integration';
import WorkflowConnectionModal from './WorkflowBuilder/components/ConnectionModal';
import { getWorkspacePath } from '../../utils/workspaceUtils';
import { border, borderRadius, fontSize, padding, width } from '../../../node_modules/@mui/system/index';

const WorkflowTemplates = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  // Redux state
  const { templates, loading, requiredConnections, isLoading } = useSelector(state => state.Workflows);
  const { tools } = useSelector(state => state.Integration);

  // Local state
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [cloneDescription, setCloneDescription] = useState('');
  const [cloning, setCloning] = useState(false);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [descriptionexpand, setDescriptionExpand] = useState(false);
  const [selectedTemplateDetails, setSelectedTemplateDetails] = useState(null);

  // Load templates and tools on mount and when filters change
  useEffect(() => {
    loadTemplates();
    dispatch(getIntegrationTools());
  }, [page, rowsPerPage, searchQuery]);

  const loadTemplates = () => {
    dispatch(listSystemTemplates({
      page: page + 1,
      limit: rowsPerPage,
      search: searchQuery
    }));
  };

  // Create tools icon map from Redux tools data
  const toolsIconMap = useMemo(() => {
    const map = {};
    if (tools?.data && Array.isArray(tools.data)) {
      tools.data.forEach(tool => {
        if (tool.name) {
          map[tool.name] = {
            logo: tool.logo,
            iconSvg: tool.iconSvg,
            description: tool.description
          };
        }
      });
    }
    return map;
  }, [tools]);

  const handleUseTemplate = (template) => {
    setSelectedTemplate(template);
    setCloneName(`${template.name} - Copy`);
    setCloneDescription(template.description || '');

    // Check required connections
    setLoadingConnections(true);
    dispatch(getRequiredConnections(template._id));
  };

  // Watch for required connections response
  useEffect(() => {
    if (requiredConnections && selectedTemplate && loadingConnections) {
      setLoadingConnections(false);

      // If all required connections are satisfied, open clone dialog
      if (requiredConnections.allConnected) {
        setCloneDialogOpen(true);
      } else {
        // Show connection modal
        setShowConnectionModal(true);
      }
    }
  }, [requiredConnections, selectedTemplate, loadingConnections]);

  const handleConnectionsComplete = () => {
    setShowConnectionModal(false);
    // After connections are complete, open clone dialog
    setCloneDialogOpen(true);
  };

  const handleCloneWorkflow = () => {
    if (!selectedTemplate) return;

    setCloning(true);
    dispatch(cloneWorkflow({
      id: selectedTemplate._id,
      name: cloneName,
      description: cloneDescription
    }));
  };

  // Watch for clone workflow success
  useEffect(() => {
    if (!isLoading && cloning) {
      // Clone completed successfully
      setCloning(false);
      setCloneDialogOpen(false);
      // Note: Navigation will be handled by the saga or we can add it here
      // For now, just close the dialog
    }
  }, [isLoading, cloning]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getComplexityColor = (complexity) => {
    switch (complexity) {
      case 'simple':
        return 'success';
      case 'medium':
        return 'warning';
      case 'complex':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleViewDetails = (template) => {
    setSelectedTemplateDetails(template);
    setDetailsDrawerOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDrawerOpen(false);
    // Clear selected template after animation completes
    setTimeout(() => setSelectedTemplateDetails(null), 300);
  };

  return (
    <>
      <div className='content template-workflow-page'>
        <div className='main-container'>
          <div className='bottom-content-area'>
            <div className='tabs-content'>
              <div className='content'>
                <div className='allemails-area'>
                  <div className='page-title-area'>
                    <div>
                      <h2>
                        <button className='back-btn' onClick={() => history.push(getWorkspacePath('/agent/workflows'))}>
                          <svg width='8' height='14' viewBox='0 0 8 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <path d='M7 13L1 7L7 1' stroke='black' strokeWidth='2' stroke-linecap='round' stroke-linejoin='round' />
                          </svg>
                        </button>
                        Workflow Templates
                      </h2>
                      {/* Description */}
                      <p>Choose from pre-built workflow templates to quickly get started. Templates can be customized after cloning.</p>
                    </div>
                  </div>
                  <div className='row'>
                    <div className='col-12'>
                      <div className="table-container filters-table">
                        <div className='header-table-filters advanced-table-filters'>
                          <>
                            <div className='left-area'>
                              <div className='filter-box'>
                                <div className='searchbox'>
                                  <TextField
                                    fullWidth
                                    hiddenLabel
                                    size="small"
                                    name='search'
                                    variant="standard"
                                    placeholder="Search templates..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                  />
                                  <i className='icon'>
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M5.82319 10.6464C3.16455 10.6464 1 8.48183 1 5.82319C1 3.16455 3.16455 1 5.82319 1C8.48183 1 10.6464 3.16455 10.6464 5.82319C10.6464 8.48183 8.48183 10.6464 5.82319 10.6464ZM5.82319 1.70583C3.55041 1.70583 1.70583 3.55511 1.70583 5.82319C1.70583 8.09127 3.55041 9.94055 5.82319 9.94055C8.09597 9.94055 9.94055 8.09127 9.94055 5.82319C9.94055 3.55511 8.09597 1.70583 5.82319 1.70583Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                                      <path d="M13.3182 13.7068C13.2245 13.7069 13.1307 13.6725 13.0566 13.5985L9.10425 9.63167C8.96097 9.48877 8.96065 9.25192 9.10355 9.10864C9.24646 8.96536 9.4833 8.96505 9.62658 9.10795L13.5789 13.0748C13.7222 13.2177 13.7225 13.4546 13.5796 13.5979C13.5057 13.672 13.412 13.7066 13.3182 13.7068Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                                    </svg>
                                  </i>
                                </div>
                              </div>
                            </div>
                          </>
                        </div>
                        <div className='agent-viewbox-main'>
                          <div className="agent-container">
                            {/* Templates Grid */}
                            {loading ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                <CircularProgress />
                              </Box>
                            ) : templates?.templates?.length === 0 ? (
                              <Paper sx={{ p: 4, textAlign: 'center' }}>
                                <Typography color="text.secondary">No templates found</Typography>
                              </Paper>
                            ) : (
                              <Grid container spacing={3}>
                                {templates?.templates?.map((template) => (
                                  <Grid item xs={12} sm={6} md={4} key={template._id} onClick={() => handleViewDetails(template)}>
                                    <Card
                                      sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        '&:hover': {
                                          transform: 'translateY(-4px)',
                                          boxShadow: 4
                                        },
                                        className: "agent-card"
                                      }}
                                    >
                                      <div className="agent-header">
                                        <h4>{template.name}</h4>
                                        {template.description && (
                                          <p> {template.description}</p>
                                        )}
                                        {/* Badges */}
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                          <Chip
                                            label={template.complexity}
                                            size="small"
                                            color={getComplexityColor(template.complexity)}
                                          />
                                          {template.price > 0 ? (
                                            <Chip
                                              icon={<MoneyIcon sx={{ fontSize: '16px !important' }} />}
                                              label={`${template.currency === 'USD' ? '$' : '₨'}${template.price.toFixed(2)}`}
                                              size="small"
                                              color="primary"
                                              variant="outlined"
                                            />
                                          ) : (
                                            <Chip label="Free" size="small" color="success" variant="outlined" />
                                          )}
                                        </Box>
                                        {/* Tags */}
                                        {template.tags && template.tags.length > 0 && (
                                          <Box sx={{ mb: 2 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                              Tags
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                              {template.tags.slice(0, 3).map((tag, index) => (
                                                <Chip
                                                  key={index}
                                                  label={tag.name || tag}
                                                  size="small"
                                                  variant="outlined"
                                                  sx={{ fontSize: '0.7rem' }}
                                                />
                                              ))}
                                              {template.tags.length > 3 && (
                                                <Chip
                                                  label={`+${template.tags.length - 3}`}
                                                  size="small"
                                                  variant="outlined"
                                                  sx={{ fontSize: '0.7rem' }}
                                                />
                                              )}
                                            </Box>
                                          </Box>
                                        )}
                                      </div>

                                      <div className={`respond-box-area`}>
                                        {/* Tools */}
                                        {template.tools && template.tools.length > 0 && (
                                          <Box sx={{ mb: 2 }}>
                                            <div className={`respond-box`}>
                                              <div className='text-bx'>
                                                <p>Integrations
                                                  <span>
                                                    {template.tools.map((toolName, index) => {
                                                      const toolData = toolsIconMap[toolName];
                                                      return (
                                                        <Tooltip key={index} title={toolName}>
                                                          {toolData?.logo ? (
                                                            <Avatar
                                                              src={toolData.logo}
                                                              sx={{ width: 28, height: 28 }}
                                                            />
                                                          ) : toolData?.iconSvg ? (
                                                            <Box
                                                              dangerouslySetInnerHTML={{ __html: toolData.iconSvg }}
                                                              sx={{
                                                                width: 28,
                                                                height: 28,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                '& svg': { width: '100%', height: '100%' }
                                                              }}
                                                            />
                                                          ) : (
                                                            <Chip
                                                              icon={<ExtensionIcon sx={{ fontSize: '16px !important' }} />}
                                                              label={toolName}
                                                              size="small"
                                                              variant="outlined"
                                                              sx={{ fontSize: '0.7rem' }}
                                                            />
                                                          )}
                                                        </Tooltip>
                                                      );
                                                    })}
                                                  </span>
                                                </p>
                                              </div>
                                            </div>
                                          </Box>
                                        )}
                                      </div>
                                      <div className={`respond-box-area style-border`}>
                                        {/* Stats */}
                                        <Box>
                                          <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                              <Box sx={{ textAlign: 'center' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                  <BuildIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                  <Typography variant="body2" fontWeight={600}>
                                                    {template.nodeCount || 0}
                                                  </Typography>
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">
                                                  Nodes
                                                </Typography>
                                              </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                              <Box sx={{ textAlign: 'center' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                  <TrendingUpIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                  <Typography variant="body2" fontWeight={600}>
                                                    {template.usageCount || 0}
                                                  </Typography>
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">
                                                  Uses
                                                </Typography>
                                              </Box>
                                            </Grid>
                                          </Grid>
                                        </Box>
                                      </div>
                                      <div className="bottom-btn-area">
                                        <button
                                          fullWidth
                                          className="btn-style-new"
                                          variant="contained"
                                          onClick={(e) => { handleUseTemplate(template); e.stopPropagation(); }}
                                          disabled={loadingConnections}
                                        >
                                          <i className="icon">
                                            <svg width="13" height="13" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '5px', verticalAlign: 'middle' }}>
                                              <path d="M15 2H5C3.89543 2 3 2.89543 3 4V16C3 17.1046 3.89543 18 5 18H15C16.1046 18 17 17.1046 17 16V4C17 2.89543 16.1046 2 15 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M7 6H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M7 10H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M7 14H10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                          </i>
                                          Use Template
                                        </button>
                                      </div>
                                    </Card>
                                  </Grid>
                                ))}
                              </Grid>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div >
      {/* Pagination */}
      {templates?.pagination && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <TablePagination
            component="div"
            count={templates.pagination.total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[6, 12, 24]}
          />
        </Box>
      )}
      {/* Connection Modal */}
      {showConnectionModal && requiredConnections && (
        <WorkflowConnectionModal
          open={showConnectionModal}
          onClose={() => setShowConnectionModal(false)}
          requiredConnections={requiredConnections.connectionStatus}
          onConnectionsComplete={handleConnectionsComplete}
          workflowName={selectedTemplate?.name}
        />
      )}

      {/* Clone Dialog */}
      <Dialog
        open={cloneDialogOpen}
        onClose={() => setCloneDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        className="workflow-clone-modal"
      >
        <DialogTitle>Create Workflow from Template</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <div className="group-form">
              <label>Workflow Name</label>
              <TextField
                fullWidth
                placeholder="Add Workflow Name Here"
                value={cloneName}
                onChange={(e) => setCloneName(e.target.value)}
                sx={{ mb: 2 }}
                required
                helperText="Give your workflow a unique name"
              />
            </div>
            <div className="group-form">
              <label>Description</label>
              <TextField
                fullWidth
                placeholder="Add Description Here"
                value={cloneDescription}
                onChange={(e) => setCloneDescription(e.target.value)}
                multiline
                rows={3}
                helperText="Optional: Describe what this workflow does"
              />
            </div>

          </Box>
        </DialogContent>
        <DialogActions>
          <button className="btn-style-border" onClick={() => setCloneDialogOpen(false)} disabled={cloning}>
            Cancel
          </button>
          <button
            className="btn-style-new"
            variant="contained"
            onClick={handleCloneWorkflow}
            disabled={cloning || !cloneName.trim()}
            startIcon={cloning ? <CircularProgress size={16} /> : <CopyIcon />}
          >
            {cloning ? 'Creating...' : 'Create Workflow'}
          </button>
        </DialogActions>
      </Dialog>

      {/* Details Drawer */}
      <Drawer
        anchor="right"
        open={detailsDrawerOpen}
        onClose={handleCloseDetails}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: '50%', md: '70%' },
            maxWidth: 750,
            p: 0
          }
        }}
      >
        {selectedTemplateDetails && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', padding: '10px 15px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    {selectedTemplateDetails.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={selectedTemplateDetails.complexity}
                      size="small"
                      color={getComplexityColor(selectedTemplateDetails.complexity)}
                    />
                    {selectedTemplateDetails.price > 0 ? (
                      <Chip
                        icon={<MoneyIcon />}
                        label={`${selectedTemplateDetails.currency === 'USD' ? '$' : '₨'}${selectedTemplateDetails.price.toFixed(2)}`}
                        size="small"
                        color="primary"
                      />
                    ) : (
                      <Chip label="Free" size="small" color="success" />
                    )}
                  </Box>
                </Box>
                <IconButton onClick={handleCloseDetails}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              <Stack spacing={3}>
                {/* Description */}
                {selectedTemplateDetails.description && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DescriptionIcon fontSize="small" />
                      Description
                    </Typography>
                    <div className={`description-box ${descriptionexpand ? "expand" : ""}`} >
                      <p style={{ fontSize: '12px' }}>{selectedTemplateDetails.description}</p>
                      <button className="see-more-btn" onClick={() => setDescriptionExpand(prev => !prev)}>
                        {descriptionexpand ? 'Hide' : 'See More'}</button>
                    </div>

                  </Box>
                )}

                {/* Tags */}
                {selectedTemplateDetails.tags && selectedTemplateDetails.tags.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TagIcon fontSize="small" />
                      Tags
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {selectedTemplateDetails.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag.name || tag}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Tools */}
                {selectedTemplateDetails.tools && selectedTemplateDetails.tools.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ExtensionIcon fontSize="small" />
                      Integrations Required
                    </Typography>
                    <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1, }}>
                      {selectedTemplateDetails.tools.map((toolName, index) => {
                        const toolData = toolsIconMap[toolName];
                        return (
                          <ListItem key={index} sx={{ border: '1px solid #E5EAF0', borderRadius: '6px', marginBottom: '10px' }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              {toolData?.logo ? (
                                <Avatar src={toolData.logo} sx={{ width: 24, height: 24 }} />
                              ) : toolData?.iconSvg ? (
                                <Box
                                  dangerouslySetInnerHTML={{ __html: toolData.iconSvg }}
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    display: 'flex',
                                    alignItems: 'center',
                                    '& svg': { width: '100%', height: '100%' }
                                  }}
                                />
                              ) : (
                                <ExtensionIcon />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={toolName}
                              secondary={toolData?.description}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                )}

                {/* Workflow Structure */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountTreeIcon fontSize="small" />
                    Workflow Structure
                  </Typography>
                  <div className="structure-area">
                    <div className="row">
                      <div className="col-lg-6 col-md-12">
                        <div className="structure-card">
                          <p>Total Nodes</p>
                          <h3>{selectedTemplateDetails.nodeCount || 0}</h3>
                        </div>
                      </div>
                      <div className="col-lg-6 col-md-12">
                        <div className="structure-card">
                          <p>Complexity</p>
                          <h3>{selectedTemplateDetails.complexity}</h3>
                        </div>
                      </div>
                    </div>
                  </div>
                </Box>

                {/* Usage Statistics */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUpIcon fontSize="small" />
                    Usage Statistics
                  </Typography>
                  <div className="structure-area">
                    <div className="row">
                      <div className="col-lg-12">
                        <div className="structure-card">
                          <p>Times Used</p>
                          <h3>{selectedTemplateDetails.usageCount || 0}</h3>
                        </div>
                      </div>
                    </div>
                  </div>
                </Box>

                {/* Metadata */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon fontSize="small" />
                    Additional Information
                  </Typography>
                  <div className="structure-area style-additional">
                    <div className="row">
                      {selectedTemplateDetails.createdBy && (
                        <div className="col-lg-4 col-md-6 col-sm-12">
                          <div className="structure-card">
                            <i className="icon"><PersonIcon /></i>
                            <div className="text-heading">
                              <p>Created By</p>
                              <h3>{`${selectedTemplateDetails.createdBy.firstName} ${selectedTemplateDetails.createdBy.lastName}`}</h3>
                            </div>
                          </div>
                        </div>
                      )}
                      {selectedTemplateDetails.createdAt && (
                        <div className="col-lg-4 col-md-6 col-sm-12">
                          <div className="structure-card">
                            <i className="icon"><CalendarIcon /></i>
                            <div className="text-heading">
                              <p>Created On</p>
                              <h3>{new Date(selectedTemplateDetails.createdAt).toLocaleDateString()}</h3>
                            </div>
                          </div>
                        </div>
                      )}
                      {selectedTemplateDetails.createdAt && (
                        <div className="col-lg-4 col-md-6 col-sm-12">
                          <div className="structure-card">
                            <i className="icon"><CalendarIcon /></i>
                            <div className="text-heading">
                              <p>Last Updated</p>
                              <h3>{new Date(selectedTemplateDetails.updatedAt).toLocaleDateString()}</h3>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Box>
              </Stack>
            </Box>

            {/* Actions */}
            <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
              <button
                variant="contained"
                className="btn-style-new"
                fullWidth
                size="large"
                style={{ width: '100%' }}
                onClick={() => {
                  handleCloseDetails();
                  handleUseTemplate(selectedTemplateDetails);
                }}
              >
                <i className="icon">
                  <svg width="13" height="13" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '5px', verticalAlign: 'middle' }}>
                    <path d="M15 2H5C3.89543 2 3 2.89543 3 4V16C3 17.1046 3.89543 18 5 18H15C16.1046 18 17 17.1046 17 16V4C17 2.89543 16.1046 2 15 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M7 6H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M7 10H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M7 14H10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </i>
                Use This Template
              </button>
            </Box>
          </Box>
        )}
      </Drawer>
    </>
  );
};

export default WorkflowTemplates;